/* eslint-disable camelcase */
/* eslint-disable import/order */
import { FirebaseMessaging, GetTokenResult, Notification, NotificationReceivedEvent } from '@capacitor-firebase/messaging'
import { Capacitor } from '@capacitor/core'
import { Badge } from '@capawesome/capacitor-badge'
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { FirebaseApp, getVapidKey } from '@apps-common/config/firebase'
import { toastPushNotification } from '@apps-common/lib/toast'
import { INotificationPayload, IPortalPushSettingsModel } from '@apps-common/types/portal/push-registration'
import { getMessaging, onMessage } from '@firebase/messaging'
import { UseMutateFunction } from '@tanstack/react-query'

let webInBackground = false
let backgroundMessagesProcessing = false
const backgroundMessages: INotificationPayload[] = []

export interface IAppNotification {
  checkPermissions: () => Promise<'prompt' | 'prompt-with-rationale' | 'granted' | 'denied'>
  requestPermissions: () => Promise<'prompt' | 'prompt-with-rationale' | 'granted' | 'denied'>
  getToken: () => Promise<GetTokenResult>
  deleteToken: () => Promise<void>
  getDeliveredNotifications: () => Promise<Notification[]>
  removeDeliveredNotifications: (notifications: Notification[]) => Promise<void>
  removeAllDeliveredNotifications: () => Promise<void>
  subscribeToNotifications: (listener: IAppNotificationListener) => void
  unsubscribeToNotifications: (id: string) => void
  subscribeToTopic: (topic: string) => Promise<void>
  unsubscribeFromTopic: (topic: string) => Promise<void>
  settingsLoading: boolean
  settings: IPortalPushSettingsModel | undefined
  updateSettings: UseMutateFunction<IPortalPushSettingsModel, Error, any, unknown>
  injectNotification: () => void
}

export interface IAppNotificationListener {
  id: string
  destination: string
  onNotificationReceived: (event: INotificationPayload) => void
  // onNotificationActionPerformed?: (event: INotificationPayload) => void
}

const checkPermissions = async () => {
  const result = await FirebaseMessaging.checkPermissions()

  return result.receive
}

const requestPermissions = async () => {
  const result = await FirebaseMessaging.requestPermissions()

  return result?.receive
}

const getToken = async (worker?: ServiceWorkerRegistration) => {
  if (Capacitor.isNativePlatform()) {
    return FirebaseMessaging.getToken()
  }

  const vapidKey = getVapidKey()

  return FirebaseMessaging.getToken({ vapidKey, serviceWorkerRegistration: worker })
}

const deleteToken = async () => {
  await FirebaseMessaging.deleteToken()
}

const getDeliveredNotifications = async () => {
  const result = await FirebaseMessaging.getDeliveredNotifications()

  return result.notifications
}

const removeDeliveredNotifications = async (notifications: Notification[]) => {
  await FirebaseMessaging.removeDeliveredNotifications({
    notifications
  })
}

export const removeAllDeliveredNotifications = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      await Badge.clear()
      console.log('Cleared app badge')
    } catch (error) {
      console.error('Failed to clear app badge', error)
    }
  }

  await FirebaseMessaging.removeAllDeliveredNotifications()
}

const subscribeToTopic = async (topic: string) => {
  await FirebaseMessaging.subscribeToTopic({ topic })
}

const unsubscribeFromTopic = async () => {
  await FirebaseMessaging.unsubscribeFromTopic({ topic: 'news' })
}

const addNotificationReceivedListener = async (listener: (event: NotificationReceivedEvent) => void) => {
  await FirebaseMessaging.addListener('notificationReceived', (event) => {
    listener(event)
  })
}

const removeAllListeners = async () => {
  await FirebaseMessaging.removeAllListeners()
}

const NotificationContext = createContext<IAppNotification | null>(null)

export const NotificationProvider = ({ pushHook, children }: any) => {
  const [listeners, setListeners] = useState<IAppNotificationListener[]>([])

  const listenersRef = useRef(listeners)

  const { settingsLoading, registrationLoading, registration, settings, updateSettings } = pushHook({
    checkPermissions,
    requestPermissions,
    getToken
  })

  useEffect(() => {
    listenersRef.current = listeners
  }, [listeners])

  const processNotification = (m: INotificationPayload) => {
    console.log('PROCESSING NOTIFICATION......')
    console.log('listeners', listenersRef.current)
    console.log('listenersRef', listenersRef.current)
    listenersRef.current.forEach((l) => {
      const { destination } = m?.data || {}

      console.log('NOTIFICATION LISTENER CHECK...', l.id, l.destination, destination)
      if (l.destination === destination) {
        l.onNotificationReceived(m)
      }
    })

    toastPushNotification(m)
  }

  const processBackgroundNotifications = () => {
    backgroundMessagesProcessing = true
    try {
      while (backgroundMessages.length > 0) {
        const m = backgroundMessages.shift()

        if (m) {
          processNotification(m)
        }
      }
    } finally {
      backgroundMessagesProcessing = false
    }
  }

  useEffect(() => {
    if (!settingsLoading && !registrationLoading && registration) {
      if (!Capacitor.isNativePlatform()) {
        console.log('REGISTERING VISIBILITY CHANGE!!!')
        document.addEventListener('visibilitychange', () => {
          const state = document.visibilityState

          if (state === 'visible') {
            webInBackground = false
          } else {
            webInBackground = true
          }

          if (!webInBackground && !backgroundMessagesProcessing) {
            processBackgroundNotifications()
          }
        })

        const FirebaseMessagingWeb = getMessaging(FirebaseApp())

        onMessage(FirebaseMessagingWeb, (payload) => {
          console.log('REVEIEVD MESSAGE', payload)

          const { messageId, notification, data, fcmOptions } = payload || {}
          const id = messageId
          const title = notification?.title
          const body = notification?.body
          const combinedData = { ...(data || {}), ...(fcmOptions || {}) }

          if (id && title && body) {
            const m = {
              id,
              title,
              body,
              data: combinedData
            }

            processNotification(m)
          }
        })

        navigator.serviceWorker.addEventListener('message', (event) => {
          const payload = event?.data

          if (payload) {
            const { notification, data } = payload
            const { title, body, click_action } = notification || {}

            const combinedData = data || {}
            const { notificationId } = combinedData

            if (!notificationId) return

            if (click_action) {
              combinedData.link = click_action
            }

            if (title && body) {
              const message = {
                id: notificationId,
                title,
                body,
                data: combinedData
              }
              const index = backgroundMessages.findIndex((m) => m?.id === notificationId)

              if (webInBackground) {
                if (index === -1) {
                  console.log('ADDING BACKGROUND NOTIFICATION: ', message)
                  backgroundMessages.push(message)
                }
              } else {
                // processNotification(message)
              }
            }
          }
        })
      }

      const onNotificationReceived = (event: NotificationReceivedEvent) => {
        const { notification } = event || {}

        if (!notification) return

        const { id, title, body, data } = notification as any

        if (id && title && body) {
          const m = {
            id,
            title,
            body,
            data
          }

          console.log('Foreground app message:', m)

          processNotification(m)
        }
      }

      const subscribe = () => {
        addNotificationReceivedListener(onNotificationReceived)
        // addNotificationActionPerformedListener(onNotificationActionPerformed)
      }

      subscribe()
    }

    return () => {
      // TODO: remove listeners for visibility and navigator service worker
      removeAllListeners()
    }
  }, [!settingsLoading && !registrationLoading && registration, listeners])

  const injectNotification = () => {
    // processNotification({
    //   id: 'testid',
    //   title: 'Control Update',
    //   body: 'Test notification for control',
    //   data: {
    //     controlId: '552',
    //     destination: 'unit-control',
    //     controlState: 'ON',
    //     controlStates: 'P1:ON:01'
    //   }
    // })
    processNotification({
      id: 'testid',
      title: 'Activation Update',
      body: 'Test notification for activation',
      data: {
        controlId: '3826',
        destination: 'unit-control',
        controlState: 'ON',
        controlStates: 'P1:ON:01'
      }
    })
  }

  const value = useMemo(
    () => ({
      checkPermissions,
      requestPermissions,
      getToken,
      deleteToken,
      getDeliveredNotifications,
      removeDeliveredNotifications,
      removeAllDeliveredNotifications,
      subscribeToNotifications: (listener: IAppNotificationListener) => {
        const index = listeners.findIndex((l) => l.id === listener.id)

        console.log('Listener requesting subscription...', listener.id, 'exists:', index !== -1, listeners)

        if (index === -1) {
          const updated = [...listeners]

          console.log('Listener added...', listener.id)

          updated.push(listener)
          setListeners(updated)
        }
      },
      unsubscribeToNotifications: (id: string) => {
        const index = listeners.findIndex((l) => l.id === id)

        if (index !== -1) {
          console.log('Updating notiofication listeners...', id)
          const updated = [...listeners.filter((l) => l.id !== id)]

          setListeners(updated)
        }
      },
      subscribeToTopic,
      unsubscribeFromTopic,
      settingsLoading,
      settings,
      updateSettings,
      injectNotification
    }),
    [settingsLoading, settings, updateSettings]
  )

  console.log('Notification Listeners:', listeners)

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
}

export const useNotifications = () => useContext(NotificationContext)
